import * as React from "react";
import {VeboxExpandable} from "../components/expadable/VeboxExpandable";
import {
    EmptyState,
    infoIcons,
    InputAddon,
    SelectInput,
    SelectInputOption,
    SpinnerSize,
    TextInput,
    Tooltip
} from "@vebox/etui-core";
import {INPUT_BORDER_BOX, SELECT_BORDER_BOX, SelectStyles, Styles} from "../components/common/etui_components_styles";
import {equalsFilter, hasFilters, UserListContext} from "./contexts/UserListContext";
import {VeboxModels} from "../types/veboxModels";
import {VeboxTreeView} from "../components/tree_view/VeboxTreeView";
import {useCommonData} from "./contexts/CommonDataProvider";
import {Display} from "../components/Display";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import {VeboxUser} from "../providers/VeboxUser";
import {Loader} from "../components/Utils/loader/Loader";
import {RestDataStatus} from "../hooks/useRestData";
import {getAllSelectedNodes, isChildSelected} from "./HierarchyVisibilityUtils";
import {isRoleMatchingWord, MANAGE_HIDDEN_USERS, userHasPermission} from "../components/Utils/RolesUtils";
import {useTheme} from "@vebox/etui-system";
import {defaultTheme} from "@vebox/etui-themes";
import useCurrentUser = VeboxUser.useCurrentUser;

declare var AppTranslations: AppTranslations;

const ENTER_KEY_CODE = 13;

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
    ...INPUT_BORDER_BOX,
    root: {
        mb: 10,
        color: "white",
        background: "#303030"
    },
};


const SELECT_BORDER_BOX_WITH_MARGIN: SelectStyles = {
    ...SELECT_BORDER_BOX,
    root: {
        mb: 10,
        color: "white",
        borderRadius: "5px",
        borderBottomRightRadius: "5px",
        borderBottomLeftRadius: "5px",
    },
    input: {
        color: "white",
        bg: "#303030",
        borderRadius: "5px",
        borderBottomRightRadius: "5px",
        borderBottomLeftRadius: "5px",
    },
    label: {
        color: "#70767E"
    }
};

export type FilterRawValues = {
    code: string,
    status: SelectInputOption,
    visibility: SelectInputOption,
    roles: string[],
    hierarchy: any[]
}

type UsersListFilterContextType = {
    values?: FilterRawValues
    updateValue: (valueName, value) => void
}

const UsersListFilterContext = React.createContext<UsersListFilterContextType>({} as any)

type UserListFilterProps = {
    onClose: () => void
}


export const UserListFilter: React.FC<UserListFilterProps> = (props) => {

    const {rawFilter, updateFilters} = React.useContext(UserListContext);

    const [values, setValues] = React.useState<FilterRawValues>();
    const [filtersChanged, setFiltersChanged] = React.useState<boolean>(false);
    const [containsFilters, setContainsFilters] = React.useState<boolean>(false);

    const updateValue = (name: string, value: any) => {
        setValues(prevValues => ({
            ...prevValues,
            [name]: value
        }))
    }

    const applyFilters = () => {
        updateFilters(values)
        props.onClose()
    }

    const resetFilters = () => {
        updateFilters(undefined as any)
        props.onClose()
    }

    React.useEffect(() => {
        setFiltersChanged(!equalsFilter(rawFilter, values))
    }, [values]);

    React.useEffect(() => {
        setValues(rawFilter);
        setContainsFilters(hasFilters(rawFilter))
    }, []);

    const handleKeyPress: React.KeyboardEventHandler<any> = (e) => {
        if (e.charCode === ENTER_KEY_CODE) {
            applyFilters()
        }
    }

    return (
        <div style={{width: "100%", position: "relative"}}>
            <div
                className={"flx-col"}
                onKeyPress={handleKeyPress}
                style={{
                    padding: 10,
                    width: "100%",
                    background: "#303030",
                    boxSizing: "border-box",
                    borderRadius: 5,
                    position: "absolute",
                    zIndex: 10
                }}
            >
                <div className={"flx-row flx-space-between padding-10"}>
                    <h1 className={"filter-dropdown-title"}>{AppTranslations.filters.filterBy}</h1>
                    <span className={"material-symbols-outlined clickable"} onClick={props.onClose}>close</span>
                </div>
                <UsersListFilterContext.Provider value={{values: values ?? rawFilter, updateValue}}>
                    <CharacteristicsFilter/>
                    <UserHierarchyFilter/>
                </UsersListFilterContext.Provider>
                <div className={"user-filter-action"}>
                    <Display isVisible={containsFilters}>
                        <div
                            className="circleButton circleButtonWhiteBorder circleButtonWhiteBorderBackgroundBlack "
                            onClick={resetFilters}
                        >
                            <div className="onButton">
                                <div className="icon iconFiltersCancel clickable"/>
                            </div>
                        </div>
                    </Display>
                    <Display isVisible={filtersChanged}>
                        <div
                            className="circleButton circleButtonWhiteBorder circleButtonWhiteBorderBackgroundBlack "
                            onClick={applyFilters}
                        >
                            <div className="onButton">
                                <div className="icon iconFiltersApply clickable"/>
                            </div>
                        </div>
                    </Display>
                </div>
            </div>
        </div>
    )
}

type FilterProps = {
    name: string,
    active: boolean,
    open: boolean
    openIfActive: boolean
}

const Filter: React.FC<FilterProps> = (props) => {
    const head = (state, setState) => {
        const toggle = () => setState(!state)
        return <h2
            onClick={toggle}
            className={"filter-sections-title flex flx-align-center " + (props.active ? "active-section" : "not-active-section")}
        >
            <div className={"filterGroupArrow " + (state ? "filterGroupArrowDown" : "")}/>
            {props.name}
        </h2>
    }
    return (
        <VeboxExpandable collapsed={head} expanded={props.openIfActive ? props.open : false}>
            <div className={"filter-section"} style={{marginBottom: 10}}>
                {props.children}
            </div>
        </VeboxExpandable>
    )
}


const CharacteristicsFilter: React.FC = () => {
    const statusOption: SelectInputOption[] = [{
        value: "true",
        label: AppTranslations.usersManagement.activeUsers
    }, {
        value: "false",
        label: AppTranslations.usersManagement.blockedUsers
    }]

    const visibilityOption: SelectInputOption[] = [{
        value: "true",
        label: AppTranslations.filters.usersFilters.visibleUsers
    }, {
        value: "false",
        label: AppTranslations.filters.usersFilters.invisibleUsers
    }]

    const {values, updateValue} = React.useContext(UsersListFilterContext)
    const user = useCurrentUser();
    const canManageHiddenUsers: boolean = React.useMemo(() => userHasPermission(user, MANAGE_HIDDEN_USERS),
        [user?.guid]);
    const updateSelect = (name: string) => (option: SelectInputOption) => updateValue(name, option)
    const updateTextInput = (name: string) => (e) => updateValue(name, e.target.value)
    const updateRoles = (selectedRoles: string[]) => updateValue("roles",
        selectedRoles)
    const [open, setOpen] = React.useState(false)
    const [openRoles, setOpenRoles] = React.useState(false)

    const clearTextInput = (name: string) => {
        return () => {
            updateValue(name, undefined);
        }
    };

    React.useEffect(() => {
        setOpen((values?.code ?? "").length > 0
            || (values?.roles ?? []).length > 0
            || !!(values?.visibility)
            || !!(values?.status))
        setOpenRoles((values?.roles ?? []).length > 0)
    }, [])

    return (
        <Filter
            name={AppTranslations.filters.usersFilters.features}
            active={(values?.code ?? "").length > 0
                || (values?.roles ?? []).length > 0
                || !!(values?.visibility)
                || !!(values?.status)
            }
            open={open}
            openIfActive={true}
        >
            <TextInput
                label={AppTranslations.filters.usersFilters.features}
                value={values?.code ?? ""}
                onChange={updateTextInput("code")}
                styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
                autoComplete={"off"}
                endInputAddon={
                    <InputAddon position={"end"}>
                        {Boolean(values?.code) && <span
                            className="material-icons-outlined clickable"
                            onClick={clearTextInput("code")}
                        >clear</span>}
                    </InputAddon>
                }
            />
            <div className={"select-styler"}>
                <SelectInput
                    name={"status"}
                    label={AppTranslations.filters.usersFilters.status}
                    options={statusOption}
                    value={values?.status}
                    styles={SELECT_BORDER_BOX_WITH_MARGIN as any}
                    onChange={updateSelect("status")}
                    clearable={true}
                />
                {canManageHiddenUsers && (
                    <SelectInput
                        name={"visibility"}
                        label={AppTranslations.filters.usersFilters.visibility}
                        options={visibilityOption}
                        value={values?.visibility}
                        styles={SELECT_BORDER_BOX_WITH_MARGIN as any}
                        onChange={updateSelect("visibility")}
                        clearable={true}
                    />
                )}
            </div>
            <RoleFilter
                selectedRoles={values?.roles ?? []}
                onChange={updateRoles}
                openIfActive={true}
                open={openRoles}
            />
        </Filter>
    )
}

type RoleFilterProps = {
    selectedRoles: string[],
    onChange: (selectedRoles: string[]) => void
    openIfActive: boolean,
    open: boolean
}

const RoleFilter: React.FC<RoleFilterProps> = (props) => {

    const theme = useTheme();
    const head = (state, setState) => {
        const toggle = () => setState(!state)
        return (
            <div onClick={toggle} className={"flx-row flx-space-between flx-align-center"}>
                <h2
                    className={"expandable-filter-title clickable"}
                    style={{marginBottom: 5}}
                >{AppTranslations.filters.usersFilters.roles}</h2>
                {!state && <div className={"expandable-symbol"}>...</div>}
            </div>
        )
    }

    const {roles: rolesManaged, roleStatus: rolesManagedState} = useCommonData();


    const toggleCheckbox = (code: string) => (e) => {
        if (e.target.checked) {
            props.onChange([...props.selectedRoles, code])
        } else {
            props.onChange(props.selectedRoles.filter(role => role !== code))
        }
    }

    const [searchWord, setSearchWord] = React.useState("")

    const updateSearchWord = e => {
        setSearchWord(e.target.value)
    }

    const clearTextInput = () => {
        setSearchWord("");
    }


    const stopPropagation: React.KeyboardEventHandler<any> = (e) => {
        e.stopPropagation()
    }

    return (
        <VeboxExpandable collapsed={head} expanded={props.openIfActive ? props.open : false}>
            <TextInput
                label={AppTranslations.filters.usersFilters.roles}
                value={searchWord}
                onChange={updateSearchWord}
                onKeyPress={stopPropagation}
                styles={{
                    ...(INPUT_BORDER_BOX_WITH_MARGIN as any),
                }}
                autoComplete={"off"}
                endInputAddon={
                    <InputAddon position={"end"}>
                        {Boolean(searchWord) && <span
                            className="material-icons-outlined clickable"
                            onClick={clearTextInput}
                        >clear</span>}
                    </InputAddon>
                }
            />
            <Display isVisible={rolesManagedState === RestDataStatus.LOADED}>
                <div className={"roles"}>
                    {rolesManaged
                        .filter(isRoleMatchingWord(searchWord))
                        .sort((a, b) => a.name > b.name ? 1 : -1).map(role => (
                            <div key={role.code} className={"role"}>
                                <input
                                    type={"checkbox"}
                                    checked={props.selectedRoles.includes(role.code)}
                                    onChange={toggleCheckbox(role.code)}
                                    className={"checkbox"}
                                />
                                {role.name}
                                {!role.visible && (<Tooltip
                                    key={role.code + " - tooltip"}
                                    message={AppTranslations.usersManagement.invisibleRole}
                                    placement={"left"}
                                >
                                    <span className="material-icons-outlined default-cursor icon">visibility_off</span>
                                </Tooltip>)}
                            </div>
                        ))}
                </div>
            </Display>
            <Display isVisible={rolesManagedState === RestDataStatus.LOADING}>
                <div className={"loader-filter-container"}>
                    <Loader
                        spinnerSize={SpinnerSize.small}
                        relativelyCentered={true}
                        color={defaultTheme.palette.common.white}
                    />
                </div>
            </Display>
        </VeboxExpandable>
    )
}

const UserHierarchyFilter: React.FC = () => {
    const theme = useTheme();
    const {hierarchiesTree = [], hierarchiesTreeStatus} = useCommonData();
    const loggedInUser = useCurrentUser();

    const {updateValue, values} = React.useContext(UsersListFilterContext)

    const [searchWord, setSearchWord] = React.useState("")
    const [open, setOpen] = React.useState(false)

    const changeHierarchy = (v) => {
        // setValue(v)
        updateValue("hierarchy", v)
    }
    const updateSearchWord = e => {
        setSearchWord(e.target.value)
    }

    const clearTextInput = () => {
        setSearchWord("");
    }
    React.useEffect(() => {
        setOpen(values?.hierarchy?.length > 0)
    }, [])


    const allSelectedNodes = getAllSelectedNodes(values?.hierarchy ?? [])

    const stopPropagation: React.KeyboardEventHandler<any> = (e) => {
        e.stopPropagation()
    }

    return (
        <Filter
            name={AppTranslations.filters.usersFilters.hierarchies}
            active={values?.hierarchy?.length > 0}
            open={open}
            openIfActive={true}
        >
            <Display isVisible={hierarchiesTreeStatus === RestDataStatus.LOADED}>
                <TextInput
                    label={AppTranslations.filters.usersFilters.hierarchies}
                    value={searchWord}
                    onChange={updateSearchWord}
                    onKeyPress={stopPropagation}
                    styles={{
                        ...(INPUT_BORDER_BOX_WITH_MARGIN as any),
                    }}
                    autoComplete={"off"}
                    endInputAddon={
                        <InputAddon position={"end"}>
                            {Boolean(searchWord) && <span
                                className="material-icons-outlined clickable"
                                onClick={clearTextInput}
                            >clear</span>}
                        </InputAddon>
                    }
                />
                <VeboxTreeView.CheckboxGroup
                    onChange={changeHierarchy}
                    values={values?.hierarchy ?? []}
                    className={"tree"}
                >
                    {(AppTranslations.userCreationProcess.allCompany).toLowerCase()
                        .includes(searchWord.toLowerCase())
                        ? <VeboxTreeView.Checkbox
                            className={"tree-node"}
                            label={<span className={"label"}>{AppTranslations.userCreationProcess.allCompany}</span>}
                            value={loggedInUser?.company?.guid}
                        />
                        : undefined
                    }
                    {hierarchiesTree
                        .map(child => filteredHierarchiesTree(child, searchWord))
                        .filter(child => child.label.toLowerCase()
                            .includes(searchWord.toLowerCase()) || child.children.length > 0)
                        .map((tree) => buildHierarchiesTree(tree, searchWord, allSelectedNodes))}
                </VeboxTreeView.CheckboxGroup>
            </Display>
            <Display isVisible={hierarchiesTreeStatus === RestDataStatus.LOADING}>
                <div className={"loader-filter-container"}>
                    <Loader
                        spinnerSize={SpinnerSize.small}
                        relativelyCentered={true}
                        color={defaultTheme.palette.common.white}
                    />
                </div>
            </Display>
            <Display isVisible={hierarchiesTreeStatus === RestDataStatus.ERROR}>
                <EmptyState>
                    <EmptyState.InfoIcon
                        styles={{
                            root: {
                                color: "inherit",
                            }
                        }}
                        glyph={infoIcons.NoContent}
                    />
                    <EmptyState.Title color="inherit">{AppTranslations.listManager.descriptionNoElement}</EmptyState.Title>
                </EmptyState>
            </Display>
        </Filter>
    )
}

function filteredHierarchiesTree(tree: VeboxModels.HierarchiesTree, searchWord: string): VeboxModels.HierarchiesTree {
    const newChildren = tree.children
        .map(child => filteredHierarchiesTree(child, searchWord))
        .filter(child => child.label.toLowerCase().includes(searchWord.toLowerCase()) || child.children.length > 0)
    return {
        ...tree,
        children: newChildren,
    }
}

function buildHierarchiesTree(tree: VeboxModels.HierarchiesTree, searchWord: string, selectedNodes: string[]) {
    const hasSelectedChildren = isChildSelected(tree, selectedNodes)
    return (
        <VeboxTreeView.CheckboxWithoutIndeterminate
            className={`tree-node`}
            label={<span className={`label ${hasSelectedChildren ? "bold" : ""}`}>{tree.label}</span>}
            key={tree.hierarchyGuid}
            value={tree.hierarchyGuid}
            expanded={searchWord.length > 0}
        >
            {(tree.children ?? []).map((subTree) => {
                return buildHierarchiesTree(subTree, searchWord, selectedNodes)
            })}
        </VeboxTreeView.CheckboxWithoutIndeterminate>
    )
}

type CommercialHierarchyNode = {
    value: string,
    children: (string | CommercialHierarchyNode)[],
}